<template>
  <div :class="$style.topicSelect" @click.stop>
    <select
      v-model="selectedTopicId"
      :class="[
        $style.topicInput,
        { [$style[selectedTopic?.name || 'Other']]: true },
      ]"
      @change="handleTopicChange"
    >
      <option v-for="topic in topics" :key="topic.id" :value="topic.id">
        {{ topic.name }}
      </option>
      <option value="new">+ New Topic</option>
    </select>
    <TopicDialog
      v-if="showDialog"
      :is-open="showDialog"
      :topic-id="editingTopicId"
      @close="closeDialog"
      @save="handleTopicSave"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { topicService } from "../services/topicService";
import TopicDialog from "./TopicDialog.vue";

const props = defineProps<{
  modelValue: string;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", topicId: string): void;
}>();

const topics = computed(() =>
  topicService.getTopics().filter((topic) => !topic.system)
);
const selectedTopicId = ref(props.modelValue);
const showDialog = ref(false);
const editingTopicId = ref<string>();

const selectedTopic = computed(() =>
  topics.value.find((topic) => topic.id === selectedTopicId.value)
);

watch(
  () => props.modelValue,
  (newValue) => {
    selectedTopicId.value = newValue;
  }
);

function handleTopicChange() {
  if (selectedTopicId.value === "new") {
    editingTopicId.value = undefined;
    showDialog.value = true;
    selectedTopicId.value = props.modelValue; // Reset selection
  } else {
    emit("update:modelValue", selectedTopicId.value);
  }
}

function closeDialog() {
  showDialog.value = false;
  editingTopicId.value = undefined;
}

function handleTopicSave(topicId: string) {
  emit("update:modelValue", topicId);
}
</script>

<style module>
.topicSelect {
  position: relative;
  min-width: 120px;
}

.topicInput {
  width: 100%;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  color: white;
  font-size: 16px;
  font-family: inherit;
  cursor: pointer;
  appearance: none;
  position: relative;
}

.topicInput:focus {
  outline: none;
  background: rgba(255, 255, 255, 0.3);
}

.topicInput option {
  background: #2c3e50;
  color: white;
}

/* Topic-specific colors */
.Coding {
  border-left: 3px solid var(--primary-color);
}

.Meeting {
  border-left: 3px solid var(--task-design);
}

.Planning {
  border-left: 3px solid var(--task-meeting);
}

.Research {
  border-left: 3px solid var(--task-research);
}

.Documentation {
  border-left: 3px solid var(--task-break);
}

.Other {
  border-left: 3px solid var(--task-other);
}

.InBetween {
  border-left: 3px solid var(--task-break);
}
</style>
